export enum SignupStep {
  MainContact = 1,
  Company = 2,
  Controllers = 3,
  Accounts = 4,
  Finish = 5,
}

export enum PlanType {
  Essentials = 6,
  Integrated = 9,
}

export const PlanTypeStringMap: Record<PlanType | string, string | PlanType> = {
  [PlanType.Essentials]: 'essentials',
  [PlanType.Integrated]: 'integrated',
  essentials: PlanType.Essentials,
  integrated: PlanType.Integrated,
};

export function planTypeToString(planType: PlanType): string {
  return PlanTypeStringMap[planType] as string;
}

export function stringToPlanType(value: string): PlanType | undefined {
  return PlanTypeStringMap[value] as PlanType | undefined;
}

export type ControllerModel = {
  FirstName: string;
  LastName: string;
  DateOfBirth: string;
  JobTitle: string;
  IsOwner?: boolean;
  SocialSecurityNumber: string;
  EmailAddress: string;
  PhoneNumber: string;
  StreetAddress: string | null;
  Suite: string | null;
  City: string | null;
  State: string | null;
  CountryId: Country | null;
  Country?: string | null;
  PostalCode: string | null;
  Key: string;
};

export enum LegalEntityType {
  Corporation = 1,
  Individual = 2,
  Llc = 3,
  Partnership = 4,
  TaxExempt = 5,
}

export enum BankAccountType {
  CorporateChecking = 3,
  CorporateSavings = 4,
}

export enum AccountCategory {
  InsuranceAgency = 1,
  MGAWholesaler = 2,
  PremiumFinanceCompany = 3,
  Association = 4,
  TechnologyPartner = 5,
  Carrier = 6,
  Other = 255,
}

export enum IntegrationType {
  AIM = 1,
  FinancePro = 2,
  Sagitta = 3,
  IMS = 4,
  ALIS = 5,
  NowCerts = 6,
  QQCatalyst = 9,
  TechCanary = 10,
  AMS360 = 11,
  JetFile = 12,
  Novidea = 13,
  Veruna = 14,
  SIG = 15,
  Powerbroker = 16,
  EpicSDK = 17,
  Nexsure = 18,
  BindHQ = 19,
  Aria = 20,
  HawkSoft = 21,
  DuckCreek = 22,
  Exzeo = 23,
  AboveIT = 24,
  Majesco = 25,
  NestSolutions = 26,
  Cogitate = 27,
  Trailblazer = 28,
  MCI = 29,
  Loro = 30,
  Surefyre = 31,
  Ricono = 32,
  InsuredMine = 33,
  MeshVI = 34,
  INTX = 35,
  Linchpin = 36,
  ExpertInsured = 37,
  Ibynd = 38,
  Finys = 39,
  NASASoft = 40,
  Damco = 41,
  BindRocket = 42,
  Sembley = 43,
  Pathway = 44,
  AgencySystemsNewton = 45,
  WeSignature = 46,
  IMT = 47,
  MapleTechnologies = 48,
  Other = 255,
}

export enum Country {
  UnitedStates = 1,
  Canada = 2,
}

export type LookUpModel<T = number> = { Id: T; DisplayName: string };

export type DropdownOption = { Value: string; Text: string };

export const isExistingSignup = (model: { CurrentStep: number | null }): boolean => model.CurrentStep != null;

export const isFirstPassSignup = (model: { CurrentStep: number | null }): boolean => model.CurrentStep == null;

export const hasCompletedStep = (currentStep: SignupStep | null, targetStep: SignupStep): boolean => {
  if (currentStep == null) return false;
  return currentStep > targetStep;
};

// Display name mappings for enums
export const LegalEntityTypeDisplayNames: Record<LegalEntityType, string> = {
  [LegalEntityType.Corporation]: 'Corporation',
  [LegalEntityType.Individual]: 'Individual/Sole Proprietorship',
  [LegalEntityType.Llc]: 'Limited Liability Company (LLC)',
  [LegalEntityType.Partnership]: 'Partnership',
  [LegalEntityType.TaxExempt]: 'Tax Exempt Organization (501C)',
};

export const BankAccountTypeDisplayNames: Record<BankAccountType, string> = {
  [BankAccountType.CorporateChecking]: 'Corporate Checking',
  [BankAccountType.CorporateSavings]: 'Corporate Savings',
};

export const AccountCategoryDisplayNames: Record<AccountCategory, string> = {
  [AccountCategory.InsuranceAgency]: 'Insurance Agency / Broker',
  [AccountCategory.MGAWholesaler]: 'MGA/Wholesaler',
  [AccountCategory.PremiumFinanceCompany]: 'Premium Finance Company',
  [AccountCategory.Association]: 'Association',
  [AccountCategory.TechnologyPartner]: 'Technology Partner',
  [AccountCategory.Carrier]: 'Carrier',
  [AccountCategory.Other]: 'Other',
};

export const IntegrationTypeDisplayNames: Record<IntegrationType, string> = {
  [IntegrationType.AIM]: 'AIM',
  [IntegrationType.FinancePro]: 'FinancePro',
  [IntegrationType.Sagitta]: 'Sagitta',
  [IntegrationType.IMS]: 'IMS',
  [IntegrationType.ALIS]: 'ALIS',
  [IntegrationType.NowCerts]: 'NowCerts',
  [IntegrationType.QQCatalyst]: 'QQ Catalyst',
  [IntegrationType.TechCanary]: 'TechCanary',
  [IntegrationType.AMS360]: 'AMS360',
  [IntegrationType.JetFile]: 'JetFile',
  [IntegrationType.Novidea]: 'Novidea',
  [IntegrationType.Veruna]: 'Veruna',
  [IntegrationType.SIG]: 'SIG',
  [IntegrationType.Powerbroker]: 'Powerbroker',
  [IntegrationType.EpicSDK]: 'Epic SDK',
  [IntegrationType.Nexsure]: 'Nexsure',
  [IntegrationType.BindHQ]: 'BindHQ',
  [IntegrationType.Aria]: 'Aria',
  [IntegrationType.HawkSoft]: 'HawkSoft',
  [IntegrationType.DuckCreek]: 'DuckCreek',
  [IntegrationType.Exzeo]: 'Exzeo',
  [IntegrationType.AboveIT]: 'Above IT',
  [IntegrationType.Majesco]: 'Majesco',
  [IntegrationType.NestSolutions]: 'Nest Solutions',
  [IntegrationType.Cogitate]: 'Cogitate',
  [IntegrationType.Trailblazer]: 'Trailblazer',
  [IntegrationType.MCI]: 'MCI',
  [IntegrationType.Loro]: 'Loro',
  [IntegrationType.Surefyre]: 'Surefyre',
  [IntegrationType.Ricono]: 'Ricono',
  [IntegrationType.InsuredMine]: 'InsuredMine',
  [IntegrationType.MeshVI]: 'Mesh VI',
  [IntegrationType.INTX]: 'INTX',
  [IntegrationType.Linchpin]: 'Linchpin',
  [IntegrationType.ExpertInsured]: 'Expert Insured',
  [IntegrationType.Ibynd]: 'Ibynd',
  [IntegrationType.Finys]: 'Finys',
  [IntegrationType.NASASoft]: 'NASA Soft',
  [IntegrationType.Damco]: 'Damco',
  [IntegrationType.BindRocket]: 'BindRocket',
  [IntegrationType.Sembley]: 'Sembley',
  [IntegrationType.Pathway]: 'Pathway',
  [IntegrationType.AgencySystemsNewton]: 'Agency Systems/Newton',
  [IntegrationType.WeSignature]: 'WeSignature',
  [IntegrationType.IMT]: 'IMT',
  [IntegrationType.MapleTechnologies]: 'Maple Technologies',
  [IntegrationType.Other]: 'Other',
};

export const CountryDisplayNames: Record<Country, string> = {
  [Country.UnitedStates]: 'United States',
  [Country.Canada]: 'Canada',
};

export const PlanTypeDisplayNames: Record<PlanType, string> = {
  [PlanType.Essentials]: 'Essentials',
  [PlanType.Integrated]: 'Integrated',
};

// Utility function to convert enum to dropdown options
export function enumToDropdownOptions<T extends number>(
  enumObj: Record<string, T | string>,
  displayNames: Record<T, string>
): DropdownOption[] {
  let otherOption: DropdownOption | undefined;
  const nonOtherOptions: DropdownOption[] = [];

  // Get list without "Other" option
  for (const value of Object.values(enumObj)) {
    if (typeof value === 'number') {
      const option = {
        Value: String(value),
        Text: displayNames[value],
      };

      if (option.Text === 'Other') {
        otherOption = option;
      } else {
        nonOtherOptions.push(option);
      }
    }
  }

  // Sort options alphabetically, keeping "Other" at the end
  nonOtherOptions.sort((a, b) => a.Text.localeCompare(b.Text));
  if (otherOption) {
    nonOtherOptions.push(otherOption);
  }

  return nonOtherOptions;
}

export type SignupFormValues = {
  // Contact Information
  FirstName: string | null;
  LastName: string | null;
  EmailAddress: string | null;
  PhoneNumber: string | null;

  // Company Information
  CompanyName: string | null;
  DoingBusinessAs: string | null;
  StreetAddress: string | null;
  Suite: string | null;
  City: string | null;
  State: string | null;
  CountryId: Country | null;
  Country?: string | null;
  PostalCode: string | null;
  TaxId: string | null;
  LegalEntityTypeId: LegalEntityType | string | null;
  Url: string | null;
  IsPublicEntity: boolean;
  StockExchange: string | null;
  StockTicker: string | null;
  AccountCategoryId: AccountCategory | null;
  HasAcceptedCreditCards: boolean;

  // Bank Information
  TrustAccountTypeId: BankAccountType | null;
  TrustRoutingNumber: string | null;
  // Canada only - combined as "0" + institutionNumber + transitNumber on submit
  InstitutionNumber: string;
  TransitNumber: string;
  TrustAccountNumber: string | null;
  OperationalAccountTypeId: BankAccountType | null;
  OperationalRoutingNumber: string | null;
  OperationalAccountNumber: string | null;

  // Subscription Information (Credit Card - Canada only)
  CreditCardName: string | null;
  CreditCardNumber: string | null;
  CreditCardMonth: string | null;
  CreditCardYear: string | null;
  CreditCardCvc: string | null;
  CreditCardPostalCode: string | null;

  // Account Information
  IntegrationTypeId: IntegrationType | null;
  OtherManagementSystem: string | null;
  MonthlyPaperCheckCount: string | null;
  MaximumSingleTransactionAmount: string | null;
  MonthlyCreditCardVolume: string | null;
  PlanTypeId: PlanType | null;
  Comments: string | null;
  DiscoverySource: string | null;
  PromotionalCode: string | null;
  Authorization: boolean;
  PrivacyPolicyAgreement: boolean;

  MerchantCategoryCode: number | null;
  Controllers: ControllerModel[];
};

export type SignupModel = Omit<
  SignupFormValues,
  'Controllers' | 'InstitutionNumber' | 'TransitNumber' | 'CreditCardMonth' | 'CreditCardYear'
> & {
  TaxId: string | null;
  TrustAccountNumber: string | null;
  OperationalAccountNumber: string | null;
  MaskedTaxId?: string | null;
  MaskedOperationalAccountNumber?: string | null;
  MaskedTrustAccountNumber?: string | null;
  MaskedCreditCardNumber?: string | null;
  CreditCardMonth?: number | null; // Backend sends as byte
  CreditCardYear?: number | null; // Backend sends as short
  CurrentStep: SignupStep | null;
  Completed?: boolean;
  AccountName?: string;
  SenderName?: string;
  ErrorMessage: string | null;
  MerchantCategoryCode: number | null;
  Controllers?: ControllerModel[];
  Owners?: ControllerModel[]; // Backend sends as Owners
};
